import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { SiX, SiFacebook, SiReddit, SiWhatsapp, SiInstagram, SiTiktok, SiWechat } from "react-icons/si";
import { FaLinkedinIn as SiLinkedin } from "react-icons/fa6";
import { Download, Copy, Link2, X as CloseIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ImpactCardExport from "@/components/ImpactCardExport";
import { CARD_SIZE, cardFilename, renderCardBlob, downloadBlob, canShareFiles, shareBlob } from "@/lib/cardImage";

export function buildCaption(card) {
  const parts = [];
  const n = card.actions_completed ?? 0;
  parts.push(`${n} ${n === 1 ? "action" : "actions"} completed`);
  if (card.time_saved_label !== undefined && card.time_saved_label !== "0m") parts.push(`${card.time_saved_label} of decision time saved`);
  if (card.money_impact !== undefined && card.money_impact > 0) parts.push(`£${card.money_impact} in self-reported impact`);
  const stats = parts.length > 1 ? `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}` : parts[0];
  return `I've been testing MinuteSwap to turn business blockers into actual decisions rather than endless AI conversations. ${stats} so far. One problem. One move.`;
}

const copyText = async (text, msg) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Couldn't copy — select and copy manually."); }
};

const open = (url) => window.open(url, "_blank", "noopener,noreferrer");

/* Smart sharing: URL platforms get the public link + caption; image platforms get the PNG via the native
   share sheet where supported, otherwise a download + copied caption with honest instructions. */
export default function SharePlatforms({ card, publicUrl, ensurePublicUrl }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);
  const [wechat, setWechat] = useState(null);
  const filesSupported = canShareFiles();
  const caption = buildCaption(card);
  const filename = cardFilename(card.display_name);

  const png = async () => {
    const blob = await renderCardBlob(ref.current);
    if (!blob) throw new Error("render failed");
    return blob;
  };
  const link = async () => publicUrl || (await ensurePublicUrl());

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (err) { if (err?.name !== "AbortError") toast.error("Sharing failed — try Download PNG or Copy Link."); }
    finally { setBusy(null); }
  };

  const imagePlatform = (name) => run(name.toLowerCase(), async () => {
    const blob = await png();
    const url = await link();
    if (filesSupported) {
      try { await shareBlob(blob, filename, { title: "My MinuteSwap Impact", text: caption, url }); return; } catch (err) { if (err?.name === "AbortError") return; }
    }
    downloadBlob(blob, filename);
    await copyText(`${caption} ${url}`, "Caption copied.");
    toast.success(`Impact Card downloaded. Open ${name} and post it when you're ready.`, { duration: 6000 });
  });

  const wechatShare = () => run("wechat", async () => {
    const url = await link();
    if (filesSupported && /Android|iPhone|iPad/i.test(navigator.userAgent)) {
      try { await shareBlob(await png(), filename, { title: "My MinuteSwap Impact", text: caption, url }); return; } catch (err) { if (err?.name === "AbortError") return; }
    }
    setWechat(url);
  });

  const platforms = [
    { key: "linkedin", label: "LinkedIn", Icon: SiLinkedin, color: "#0A66C2", action: () => run("linkedin", async () => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(await link())}`)) },
    { key: "x", label: "X", Icon: SiX, color: "#ffffff", action: () => run("x", async () => open(`https://x.com/intent/post?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(await link())}`)) },
    { key: "facebook", label: "Facebook", Icon: SiFacebook, color: "#1877F2", action: () => run("facebook", async () => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(await link())}&quote=${encodeURIComponent(caption)}`)) },
    { key: "reddit", label: "Reddit", Icon: SiReddit, color: "#FF4500", action: () => run("reddit", async () => open(`https://www.reddit.com/submit?url=${encodeURIComponent(await link())}&title=${encodeURIComponent("How I turned business blockers into completed actions with MinuteSwap")}`)) },
    { key: "whatsapp", label: "WhatsApp", Icon: SiWhatsapp, color: "#25D366", action: () => run("whatsapp", async () => open(`https://wa.me/?text=${encodeURIComponent(`${caption} ${await link()}`)}`)) },
    { key: "instagram", label: "Instagram", Icon: SiInstagram, color: "#E4405F", action: () => imagePlatform("Instagram") },
    { key: "tiktok", label: "TikTok", Icon: SiTiktok, color: "#ffffff", action: () => imagePlatform("TikTok") },
    { key: "wechat", label: "WeChat", Icon: SiWechat, color: "#07C160", action: wechatShare },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2" data-testid="share-platforms">
        {platforms.map((p) => (
          <button key={p.key} data-testid={`share-${p.key}`} onClick={p.action} disabled={!!busy} aria-label={`Share to ${p.label}`}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-[var(--surface-raised)] px-2 py-3 transition-colors hover:border-white/25 disabled:opacity-60">
            <p.Icon size={18} color={p.color} />
            <span className="text-[11px] text-neutral-300">{busy === p.key ? "…" : p.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="download-png-btn" disabled={!!busy} onClick={() => run("download", async () => { downloadBlob(await png(), filename); toast.success("Impact Card downloaded."); })} className="btn-amber w-full">
          <Download className="mr-1 h-4 w-4" /> {busy === "download" ? "Rendering…" : "Download PNG"}
        </Button>
        <Button data-testid="copy-link-btn" disabled={!!busy} variant="outline" onClick={() => run("copy", async () => copyText(await link(), "Link copied."))} className="w-full border-white/15 text-white hover:bg-white/5">
          <Link2 className="mr-1 h-4 w-4" /> Copy Link
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-[var(--surface-raised)] p-3" data-testid="share-caption-box">
        <div className="flex items-center justify-between">
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-neutral-500">Suggested caption</span>
          <button data-testid="copy-caption-btn" onClick={() => copyText(caption, "Caption copied.")} className="inline-flex items-center gap-1 text-xs text-[var(--amber)] hover:underline">
            <Copy className="h-3 w-3" /> COPY CAPTION
          </button>
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-neutral-300" data-testid="share-caption">{caption}</p>
      </div>

      {publicUrl && (
        <div className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-white/10 bg-[var(--surface-raised)] p-2">
          <span className="min-w-0 flex-1 truncate px-2 text-xs text-neutral-400" data-testid="share-public-url">{publicUrl}</span>
        </div>
      )}

      {wechat && (
        <div className="relative rounded-xl border border-[#07C160]/40 bg-[#07C160]/[0.06] p-4 text-center" data-testid="wechat-panel">
          <button data-testid="wechat-close" onClick={() => setWechat(null)} className="absolute right-2 top-2 text-neutral-500 hover:text-white"><CloseIcon className="h-4 w-4" /></button>
          <span className="eyebrow">Scan to share in WeChat</span>
          <div className="mx-auto mt-3 inline-block rounded-lg bg-white p-2"><QRCodeSVG value={wechat} size={148} /></div>
          <p className="mt-2 text-xs text-neutral-400">Open WeChat → Discover → Scan, then forward the Impact page to a chat or Moments.</p>
          <Button data-testid="wechat-copy-link" size="sm" variant="outline" onClick={() => copyText(wechat, "Link copied.")} className="mt-3 border-white/15 text-white hover:bg-white/5">
            <Link2 className="mr-1 h-3.5 w-3.5" /> Copy Link
          </Button>
        </div>
      )}

      {createPortal(
        <div aria-hidden="true" style={{ position: "fixed", left: -20000, top: 0, width: CARD_SIZE, height: CARD_SIZE, pointerEvents: "none", zIndex: -1 }}>
          <ImpactCardExport data={card} forwardRef={ref} />
        </div>,
        document.body,
      )}
    </div>
  );
}
